import { useMemo, useState, useRef, useEffect } from "react";
import {
  CaretUpIcon,
  CaretDownIcon,
  CaretRightIcon,
  CaretLeftIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { bucketUrlPrefix } from "common/config";
import {
  ComplexCategoriesData,
  ComplexSpacesLevelOption,
  TaggingCategoryData,
} from "common/types";
import { VenueDetailsBySlugResponse, useI18N, useZoomDetection } from "common/hooks";
import { useDarkMode } from "@/hooks/useDarkMode";

type CategorySidebarProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: number | null;
  setSelectedCategory: (categoryId: number | null) => void;
  expandedCategories: Set<number>;
  setExpandedCategories: React.Dispatch<React.SetStateAction<Set<number>>>;
  showMobileMenu: boolean;
  setShowMobileMenu: (show: boolean) => void;
  onCategorySelect?: (categoryId: number, categoryName: string) => void;
  onSpaceSelect?: (
    space: ComplexSpacesLevelOption,
    categoryId: number,
    categoryName: string,
  ) => void;
  searchPlaceholder?: string;
  filteredReviewTags?: (ComplexCategoriesData | TaggingCategoryData)[];
  pageName?: string; // For analytics tracking
  filterSlot?: React.ReactNode; // Optional filter buttons rendered between search and category tree
  onNavTreeClose?: () => void; // Analytics: the sidebar's own close (X) was tapped (nav_tree_closed). Parent owns venue context.
  onSearchFiltered?: (resultsCount: number, query: string) => void; // Analytics: debounced nav_tree list_filtered. Parent owns venue context.
};

/**
 * A space belongs in the nav-tree spaces flyout only when it has something to open — mirroring the
 * guide/photos table's `hasContent` rule (accessibility option titles or photos). A space that has a
 * review_levels row but no review_details (no option titles) and no photos is informational-only and
 * must be hidden here too, so the flyout stays in sync with the table on the right. The host prunes
 * levelOptions to the visible set, but only two levels deep; the tree recurses further, so re-apply
 * the rule here to catch deeper spaces regardless of nesting. "No bathroom onsite" is excluded from
 * navigation entirely — its name is the whole message and tapping it opens nothing.
 */
const spaceHasNavigableContent = (space: ComplexSpacesLevelOption): boolean =>
  space.level !== "No bathroom onsite" &&
  ((Array.isArray(space.level_option_titles) &&
    space.level_option_titles.length > 0) ||
    (typeof space.level_photo_count === "number" && space.level_photo_count > 0));

const getNavigableSpaces = (
  spaces?: ComplexSpacesLevelOption[],
): ComplexSpacesLevelOption[] | undefined =>
  spaces?.filter(spaceHasNavigableContent);

function CategorySidebar({
  venueDetailsResponse,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  expandedCategories,
  setExpandedCategories,
  showMobileMenu,
  setShowMobileMenu,
  onCategorySelect,
  onSpaceSelect,
  searchPlaceholder = "Search Space name",
  filteredReviewTags,
  pageName = "Accessibility Guide",
  filterSlot,
  onNavTreeClose,
  onSearchFiltered,
}: CategorySidebarProps) {
  const i18n = useI18N();
  const isDark = useDarkMode();

  // Debounced search tracking
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isZoomedIn = useZoomDetection(1.25);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showMobileMenu) setMobileSpacesView(null);
  }, [showMobileMenu]);

  // Use provided filteredReviewTags or fall back to all review tags
  const reviewTags = useMemo(
    () => filteredReviewTags || venueDetailsResponse?.review_tags || [],
    [filteredReviewTags, venueDetailsResponse?.review_tags],
  );

  // Auto-expand categories when search matches lower-level items
  useEffect(() => {
    const query = searchTerm.toLowerCase().trim();
    
    if (query && reviewTags.length > 0) {
      // Collect all category IDs that should be expanded based on search results
      const categoriesToExpand = new Set<number>();
      
      reviewTags.forEach((category) => {
        // Always expand top-level category if it has filtered content
        if (category.categoryOptions && category.categoryOptions.length > 0) {
          categoriesToExpand.add(category.id);
          
          // CheckIcon if any second-level or third-level categories have content
          category.categoryOptions.forEach((option) => {
            if (option.pageChildrenCategories && option.pageChildrenCategories.length > 0) {
              // Expand the second level if it has matching subcategories
              if (option.pageTitleId) {
                categoriesToExpand.add(option.pageTitleId);
              }
            }
          });
        }
      });
      
      // Only update categories that aren't already expanded
      const hasNewCategories = Array.from(categoriesToExpand).some(
        (catId) => !expandedCategories.has(catId)
      );
      
      if (hasNewCategories) {
        setExpandedCategories((prev) => {
          const newSet = new Set(prev);
          categoriesToExpand.forEach((catId) => newSet.add(catId));
          return newSet;
        });
      }
    }
    // We only want to react to search term and filtered results changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, reviewTags]);

  // Roving tabindex state for tree navigation
  const [focusedItemId, setFocusedItemId] = useState<number | null>(null);
  const treeItemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const hoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hoveredSpacesCategory, setHoveredSpacesCategory] = useState<{
    categoryId: number;
    categoryName: string;
    spaces: ComplexSpacesLevelOption[];
  } | null>(null);
  const [spacesPanelBounds, setSpacesPanelBounds] = useState({
    top: 0,
    left: 0,
    height: 0,
  });
  const [mobileSpacesView, setMobileSpacesView] = useState<{
    spaces: ComplexSpacesLevelOption[];
    categoryId: number;
    categoryName: string;
  } | null>(null);

  // Helper to collect all visible tree item IDs in DOM order
  const getVisibleItemIds = useMemo(() => {
    const ids: number[] = [];
    
    const collectIds = (
      category: ComplexCategoriesData | TaggingCategoryData,
      skipDuplicates: boolean = false
    ) => {
      ids.push(category.id);
      
      if (expandedCategories.has(category.id) && category.categoryOptions) {
        category.categoryOptions.forEach((option) => {
          const shouldSkipLevel = option.pageTitle === category.name;
          
          if (shouldSkipLevel && option.pageChildrenCategories) {
            // Skip this level, render children directly
            option.pageChildrenCategories.forEach((subCategory) => {
              collectIds(subCategory, false);
            });
          } else {
            // Normal rendering
            ids.push(option.pageTitleId);
            
            if (expandedCategories.has(option.pageTitleId) && option.pageChildrenCategories) {
              option.pageChildrenCategories
                .filter((subCategory) => subCategory.name !== option.pageTitle)
                .forEach((subCategory) => {
                  collectIds(subCategory, false);
                });
            }
          }
        });
      }
    };
    
    reviewTags.forEach((category) => collectIds(category));
    return ids;
  }, [reviewTags, expandedCategories]);

  // nav_tree.search_input_field -> list_filtered (debounced 500ms, deduped per pause).
  // Analytics is delegated to the parent via onSearchFiltered (it owns venue context and the
  // nav_tree page); other host screens don't pass it, so they stay silent. filter_results_count
  // = the post-filter visible item count (getVisibleItemIds reflects filteredReviewTags).
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const prevSearchRef = useRef("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);
  useEffect(() => {
    if (debouncedSearch.trim() && debouncedSearch !== prevSearchRef.current) {
      prevSearchRef.current = debouncedSearch;
      onSearchFiltered?.(getVisibleItemIds.length, debouncedSearch);
    } else if (!debouncedSearch.trim()) {
      prevSearchRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Helper functions for category management
  const toggleExpanded = (categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Helper to close mobile menu only on mobile screens
  const closeMobileMenuIfMobile = () => {
    // CheckIcon if screen is mobile (below md breakpoint: 768px)
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      setShowMobileMenu(false);
    }
  };

  const scrollToCategory = (categoryName: string) => {
    setTimeout(() => {
      const categoryId = `category-${categoryName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`;
      const element = document.getElementById(categoryId);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }, 100);
  };

  const getCategoryOptionSpaces = (categoryOption: {
    pageTitleId: number;
    levelOptions?: ComplexSpacesLevelOption[];
    pageChildrenCategories?: TaggingCategoryData[];
  }) => {
    if (categoryOption.levelOptions?.length) {
      return categoryOption.levelOptions;
    }

    const wrapperChild = categoryOption.pageChildrenCategories?.[0];
    if (
      categoryOption.pageChildrenCategories?.length === 1 &&
      wrapperChild?.id === categoryOption.pageTitleId &&
      wrapperChild.levelOptions?.length
    ) {
      return wrapperChild.levelOptions;
    }

    return undefined;
  };

  const handleSpacesHover = (
    event: React.MouseEvent<HTMLElement>,
    categoryId: number,
    categoryName: string,
    spaces?: ComplexSpacesLevelOption[],
  ) => {
    // Filter out informational-only spaces that have no detail to show (no accessibility
    // review_details and no photos), matching the guide/photos table on the right.
    const navigableSpaces = getNavigableSpaces(spaces);
    if (!onSpaceSelect || !navigableSpaces?.length || window.innerWidth < 1024) return;
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }

    const sidebarRect = sidebarRef.current?.getBoundingClientRect();
    const rowRect = event.currentTarget.getBoundingClientRect();
    setSpacesPanelBounds({
      top: sidebarRect?.top ?? rowRect.top,
      left: sidebarRect?.right ?? rowRect.right,
      height: sidebarRect?.height ?? window.innerHeight - rowRect.top,
    });
    setHoveredSpacesCategory({ categoryId, categoryName, spaces: navigableSpaces });
  };

  const handleSpacesHoverLeave = () => {
    hoverCloseTimerRef.current = setTimeout(() => {
      setHoveredSpacesCategory(null);
      hoverCloseTimerRef.current = null;
    }, 120);
  };

  const keepSpacesPanelOpen = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };

  const handleSpaceSelect = (space: ComplexSpacesLevelOption) => {
    if (!hoveredSpacesCategory) return;
    setHoveredSpacesCategory(null);
    setFocusedItemId(hoveredSpacesCategory.categoryId);
    setSelectedCategory(hoveredSpacesCategory.categoryId);
    onSpaceSelect?.(
      space,
      hoveredSpacesCategory.categoryId,
      hoveredSpacesCategory.categoryName,
    );
  };

  const handleMobileSpaceSelect = (space: ComplexSpacesLevelOption) => {
    if (!mobileSpacesView) return;
    setMobileSpacesView(null);
    setFocusedItemId(mobileSpacesView.categoryId);
    setSelectedCategory(mobileSpacesView.categoryId);
    onSpaceSelect?.(space, mobileSpacesView.categoryId, mobileSpacesView.categoryName);
  };

  // Tree keyboard navigation handler
  const handleTreeKeyDown = (e: React.KeyboardEvent) => {
    const visibleIds = getVisibleItemIds;
    const currentIndex = focusedItemId !== null ? visibleIds.indexOf(focusedItemId) : -1;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < visibleIds.length - 1) {
          const nextId = visibleIds[currentIndex + 1];
          setFocusedItemId(nextId);
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          const prevId = visibleIds[currentIndex - 1];
          setFocusedItemId(prevId);
        }
        break;
        
      case 'ArrowRight':
        e.preventDefault();
        if (focusedItemId !== null) {
          if (expandedCategories.has(focusedItemId)) {
            // Already expanded - move to first child
            if (currentIndex < visibleIds.length - 1) {
              const nextId = visibleIds[currentIndex + 1];
              setFocusedItemId(nextId);
            }
          } else {
            // Expand this category
            toggleExpanded(focusedItemId);
          }
        }
        break;
        
      case 'ArrowLeft':
        e.preventDefault();
        if (focusedItemId !== null && expandedCategories.has(focusedItemId)) {
          // Collapse if expanded
          toggleExpanded(focusedItemId);
        }
        break;
        
      case 'Home':
        e.preventDefault();
        if (visibleIds.length > 0) {
          setFocusedItemId(visibleIds[0]);
        }
        break;
        
      case 'End':
        e.preventDefault();
        if (visibleIds.length > 0) {
          setFocusedItemId(visibleIds[visibleIds.length - 1]);
        }
        break;
        
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedItemId !== null) {
          const ref = treeItemRefs.current.get(focusedItemId);
          if (ref) {
            ref.click();
          }
        }
        break;
    }
  };

  // Focus the focused item when it changes
  useEffect(() => {
    if (focusedItemId !== null) {
      const ref = treeItemRefs.current.get(focusedItemId);
      if (ref) {
        ref.focus();
      }
    }
  }, [focusedItemId]);

  // Set initial focus to first item when tree is mounted
  useEffect(() => {
    if (focusedItemId === null && getVisibleItemIds.length > 0) {
      setFocusedItemId(getVisibleItemIds[0]);
    }
  }, [focusedItemId, getVisibleItemIds]);

  // Recursive component for rendering nested categories
  const renderCategory = (
    category: ComplexCategoriesData | TaggingCategoryData,
    level: number = 0,
  ) => {
    // CheckIcon if category has children - always show expand if categoryOptions exist
    // The actual filtering of duplicates happens during rendering
    const hasChildren =
      category.categoryOptions && category.categoryOptions.length > 0;
    const paddingLeft =
      level === 0 ? "pl-4" : `pl-[${52 + (level - 1) * 24}px]`;
    const verticalBarLeft =
      level === 0 ? "left-[27px]" : `left-[${27 + level * 24}px]`;

    return (
      <div key={category.id} className="relative">
        {/* Category Item */}
        <button
          type="button"
          role="treeitem"
          aria-level={level + 1}
          aria-expanded={hasChildren ? expandedCategories.has(category.id) : undefined}
          aria-selected={selectedCategory === category.id}
          tabIndex={focusedItemId === category.id ? 0 : -1}
          ref={(el) => {
            if (el) {
              treeItemRefs.current.set(category.id, el);
            } else {
              treeItemRefs.current.delete(category.id);
            }
          }}
          className={`flex items-center gap-3 ${paddingLeft} pr-4 py-3 relative w-full text-left transition-colors focus-blue ${
            selectedCategory === category.id ? "bg-container-brand-minimal" : ""
          }`}
          onMouseEnter={(event) => {
            if (!hasChildren) {
              handleSpacesHover(
                event,
                category.id,
                category.name,
                (category as TaggingCategoryData).levelOptions,
              );
            }
          }}
          onMouseLeave={handleSpacesHoverLeave}
          onClick={() => {
            setFocusedItemId(category.id);
            setSelectedCategory(category.id);
            if (hasChildren) {
              // If category has children, just expand it without navigating
              toggleExpanded(category.id);
            } else {
              // Mobile: show spaces slide-over instead of navigating
              const levelOptions = (category as TaggingCategoryData).levelOptions;
              const navigableLevelOptions = getNavigableSpaces(levelOptions);
              if (window.innerWidth < 1024 && onSpaceSelect && navigableLevelOptions?.length) {
                setMobileSpacesView({
                  spaces: navigableLevelOptions,
                  categoryId: category.id,
                  categoryName: category.name,
                });
                return;
              }
              // Only navigate and close menu if category has no children (leaf node)
              scrollToCategory(category.name);
              closeMobileMenuIfMobile();
              onCategorySelect?.(category.id, category.name);
            }
          }}
          aria-label={category.name}
        >
          {/* Category Icon - only show for top level */}
          {level === 0 && category.image && (
            <img
              src={
                bucketUrlPrefix +
                (isDark && category.image_selected
                  ? category.image_selected
                  : category.image)
              }
              alt={category.name}
              className={`w-6 h-6 flex-shrink-0 ${isDark && !category.image_selected ? "filter brightness-0 invert" : ""}`}
              aria-hidden="true"
            />
          )}

          {/* Category Name */}
          <span
            className={`flex-1 ${level === 0 ? "label-m-semibold" : "label-m-medium"} ${
              selectedCategory === category.id
                ? "text-fg-brand-subtle"
                : "text-fg-neutral-strong"
            }`}
          >
            {category.name}
          </span>

          {/* Expand/Collapse Button */}
          {hasChildren && (
            expandedCategories.has(category.id)
              ? <CaretUpIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
              : <CaretDownIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
          )}
        </button>

        {/* Subcategories */}
        {expandedCategories.has(category.id) && category.categoryOptions && (
          <div role="group" className="bg-bg-brand-secondary-default relative">
            {/* Vertical line for subcategories - always show for level 0 and above */}
            {level >= 0 && (
              <div
                className={`absolute ${verticalBarLeft} top-0 bottom-0 w-[2px] `}
              ></div>
            )}
            {category.categoryOptions.map((categoryOption) => {
              // Skip this level if the pageTitle matches the parent category name
              const shouldSkipLevel =
                categoryOption.pageTitle === category.name;

              // If we should skip this level, render children directly
              if (shouldSkipLevel && categoryOption.pageChildrenCategories) {
                return categoryOption.pageChildrenCategories.map(
                  (subCategory) => (
                    <div key={subCategory.id} className="relative group">
                      {/* Vertical bar highlight */}
                      <div
                        className={`absolute ${verticalBarLeft} top-0 bottom-0 w-[2px] transition-colors ${
                          selectedCategory === subCategory.id
                            ? "bg-[#352FC3]"
                            : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                        }`}
                      ></div>
                      <button
                        type="button"
                        role="treeitem"
                        aria-level={level + 2}
                        aria-expanded={
                          subCategory.categoryOptions && subCategory.categoryOptions.length > 0
                            ? expandedCategories.has(subCategory.id)
                            : undefined
                        }
                        aria-selected={selectedCategory === subCategory.id}
                        tabIndex={focusedItemId === subCategory.id ? 0 : -1}
                        ref={(el) => {
                          if (el) {
                            treeItemRefs.current.set(subCategory.id, el);
                          } else {
                            treeItemRefs.current.delete(subCategory.id);
                          }
                        }}
                        className={`flex items-center gap-3 pl-[52px] pr-4 py-3 relative w-full text-left transition-colors focus-blue ${
                          selectedCategory === subCategory.id
                            ? "bg-container-brand-minimal"
                            : "bg-bg-brand-secondary-default"
                        }`}
                        onMouseEnter={(event) => {
                          const hasChildren =
                            subCategory.categoryOptions &&
                            subCategory.categoryOptions.length > 0;
                          if (!hasChildren) {
                            handleSpacesHover(
                              event,
                              subCategory.id,
                              subCategory.name,
                              subCategory.levelOptions,
                            );
                          }
                        }}
                        onMouseLeave={handleSpacesHoverLeave}
                        onClick={() => {
                          setFocusedItemId(subCategory.id);
                          setSelectedCategory(subCategory.id);
                          const hasChildren =
                            subCategory.categoryOptions &&
                            subCategory.categoryOptions.length > 0;

                          if (hasChildren) {
                            // If has children, just expand without navigating
                            toggleExpanded(subCategory.id);
                          } else {
                            const navigableLevelOptions = getNavigableSpaces(subCategory.levelOptions);
                            if (window.innerWidth < 1024 && onSpaceSelect && navigableLevelOptions?.length) {
                              setMobileSpacesView({
                                spaces: navigableLevelOptions,
                                categoryId: subCategory.id,
                                categoryName: subCategory.name,
                              });
                              return;
                            }
                            // Only navigate and close menu if no children (leaf node)
                            scrollToCategory(subCategory.name);
                            closeMobileMenuIfMobile();
                            onCategorySelect?.(
                              subCategory.id,
                              subCategory.name,
                            );
                          }
                        }}
                        aria-label={subCategory.name}
                      >
                        <div
                          className={`absolute left-[40px] top-0 bottom-0 w-[2px] transition-colors ${
                            selectedCategory === subCategory.id
                              ? "bg-[#352FC3]"
                              : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                          }`}
                        ></div>
                        <span
                          className={`flex-1 label-m-medium ${
                            selectedCategory === subCategory.id
                              ? "text-fg-brand-subtle"
                              : "text-fg-neutral-strong"
                          }`}
                        >
                          {subCategory.name}
                        </span>
                        {subCategory.categoryOptions &&
                          subCategory.categoryOptions.length > 0 && (
                            expandedCategories.has(subCategory.id)
                              ? <CaretUpIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                              : <CaretDownIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                          )}
                      </button>
                      {expandedCategories.has(subCategory.id) &&
                        subCategory.categoryOptions && (
                          <div role="group" className="bg-bg-brand-secondary-default">
                            {renderCategory(subCategory, level + 1)}
                          </div>
                        )}
                    </div>
                  ),
                );
              }

              // Normal rendering if not skipping
              return (
                <div key={categoryOption.pageTitleId}>
                  {/* Level 2: Category Option (pageTitle) */}
                  <button
                    type="button"
                    role="treeitem"
                    aria-level={level + 2}
                    aria-expanded={
                      categoryOption.pageChildrenCategories &&
                      categoryOption.pageChildrenCategories.filter(
                        (subCategory) => subCategory.name !== categoryOption.pageTitle
                      ).length > 0
                        ? expandedCategories.has(categoryOption.pageTitleId)
                        : undefined
                    }
                    aria-selected={selectedCategory === categoryOption.pageTitleId}
                    tabIndex={focusedItemId === categoryOption.pageTitleId ? 0 : -1}
                    ref={(el) => {
                      if (el) {
                        treeItemRefs.current.set(categoryOption.pageTitleId, el);
                      } else {
                        treeItemRefs.current.delete(categoryOption.pageTitleId);
                      }
                    }}
                    className={`flex items-center gap-3 pl-[52px] pr-4 py-3 relative group w-full text-left transition-colors focus-blue ${
                      selectedCategory === categoryOption.pageTitleId
                        ? "bg-container-brand-minimal"
                        : "bg-bg-brand-secondary-default"
                    }`}
                    onMouseEnter={(event) => {
                      const hasNonDuplicateChildren =
                        categoryOption.pageChildrenCategories &&
                        categoryOption.pageChildrenCategories.filter(
                          (subCategory) =>
                            subCategory.name !== categoryOption.pageTitle,
                        ).length > 0;
                      if (!hasNonDuplicateChildren) {
                        handleSpacesHover(
                          event,
                          categoryOption.pageTitleId,
                          categoryOption.pageTitle,
                          getCategoryOptionSpaces(categoryOption),
                        );
                      }
                    }}
                    onMouseLeave={handleSpacesHoverLeave}
                    onClick={() => {
                      setFocusedItemId(categoryOption.pageTitleId);
                      setSelectedCategory(categoryOption.pageTitleId);
                      // CheckIcon if this level has actual children (after filtering duplicates)
                      const hasNonDuplicateChildren =
                        categoryOption.pageChildrenCategories &&
                        categoryOption.pageChildrenCategories.filter(
                          (subCategory) =>
                            subCategory.name !== categoryOption.pageTitle,
                        ).length > 0;

                      if (hasNonDuplicateChildren) {
                        // If has children, just expand without navigating
                        toggleExpanded(categoryOption.pageTitleId);
                      } else {
                        const levelOptions = getCategoryOptionSpaces(categoryOption);
                        const navigableLevelOptions = getNavigableSpaces(levelOptions);
                        if (window.innerWidth < 1024 && onSpaceSelect && navigableLevelOptions?.length) {
                          setMobileSpacesView({
                            spaces: navigableLevelOptions,
                            categoryId: categoryOption.pageTitleId,
                            categoryName: categoryOption.pageTitle,
                          });
                          return;
                        }
                        // Only navigate and close menu if no children (leaf node)
                        scrollToCategory(categoryOption.pageTitle);
                        closeMobileMenuIfMobile();
                        onCategorySelect?.(
                          categoryOption.pageTitleId,
                          categoryOption.pageTitle,
                        );
                      }
                    }}
                    aria-label={categoryOption.pageTitle}
                  >
                    {/* Vertical bar highlight on hover and selected */}
                    <div
                      className={`absolute ${verticalBarLeft} top-0 bottom-0 w-[2px] transition-colors ${
                        selectedCategory === categoryOption.pageTitleId
                          ? "bg-[#352FC3]"
                          : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                      }`}
                    ></div>

                    {/* Category Option Name */}
                    <span
                      className={`flex-1 label-m-medium ${
                        selectedCategory === categoryOption.pageTitleId
                          ? "text-fg-brand-subtle"
                          : "text-fg-neutral-strong"
                      }`}
                    >
                      {categoryOption.pageTitle}
                    </span>

                    {/* Expand/Collapse Button for Level 2 - only show if there are non-duplicate children */}
                    {categoryOption.pageChildrenCategories &&
                      categoryOption.pageChildrenCategories.filter(
                        (subCategory) =>
                          subCategory.name !== categoryOption.pageTitle,
                      ).length > 0 && (
                        expandedCategories.has(categoryOption.pageTitleId)
                          ? <CaretUpIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                          : <CaretDownIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                      )}
                  </button>

                  {/* Level 3: Page Children Categories */}
                  {expandedCategories.has(categoryOption.pageTitleId) &&
                    categoryOption.pageChildrenCategories && (
                      <div role="group" className="bg-bg-brand-secondary-default relative">
                        {/* Vertical line for level 3 */}
                        <div className="absolute left-[51px] top-0 bottom-0 w-[2px] bg-[#DEDDF7]"></div>
                        {categoryOption.pageChildrenCategories
                          .filter(
                            (subCategory) =>
                              subCategory.name !== categoryOption.pageTitle,
                          )
                          .map((subCategory) => (
                            <div
                              key={subCategory.id}
                              className="relative group"
                            >
                              {/* Vertical bar highlight on hover and selected for level 3 */}
                              <div
                                className={`absolute left-[51px] top-0 bottom-0 w-[2px] transition-colors ${
                                  selectedCategory === subCategory.id
                                    ? "bg-[#352FC3]"
                                    : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                                }`}
                              ></div>
                              {/* Level 3 Category Item */}
                              <button
                                type="button"
                                role="treeitem"
                                aria-level={level + 3}
                                aria-expanded={
                                  subCategory.categoryOptions && subCategory.categoryOptions.length > 0
                                    ? expandedCategories.has(subCategory.id)
                                    : undefined
                                }
                                aria-selected={selectedCategory === subCategory.id}
                                tabIndex={focusedItemId === subCategory.id ? 0 : -1}
                                ref={(el) => {
                                  if (el) {
                                    treeItemRefs.current.set(subCategory.id, el);
                                  } else {
                                    treeItemRefs.current.delete(subCategory.id);
                                  }
                                }}
                                className={`flex items-center gap-3 pl-[76px] pr-4 py-3 relative w-full text-left transition-colors focus-blue ${
                                  selectedCategory === subCategory.id
                                    ? "bg-container-brand-minimal"
                                    : "bg-bg-brand-secondary-default"
                                }`}
                                onMouseEnter={(event) => {
                                  const hasChildren =
                                    subCategory.categoryOptions &&
                                    subCategory.categoryOptions.length > 0;
                                  if (!hasChildren) {
                                    handleSpacesHover(
                                      event,
                                      subCategory.id,
                                      subCategory.name,
                                      subCategory.levelOptions,
                                    );
                                  }
                                }}
                                onMouseLeave={handleSpacesHoverLeave}
                                onClick={() => {
                                  setFocusedItemId(subCategory.id);
                                  setSelectedCategory(subCategory.id);
                                  const hasChildren =
                                    subCategory.categoryOptions &&
                                    subCategory.categoryOptions.length > 0;

                                  if (hasChildren) {
                                    // If has children, just expand without navigating
                                    toggleExpanded(subCategory.id);
                                  } else {
                                    const navigableLevelOptions = getNavigableSpaces(subCategory.levelOptions);
                                    if (window.innerWidth < 1024 && onSpaceSelect && navigableLevelOptions?.length) {
                                      setMobileSpacesView({
                                        spaces: navigableLevelOptions,
                                        categoryId: subCategory.id,
                                        categoryName: subCategory.name,
                                      });
                                      return;
                                    }
                                    // Only navigate and close menu if no children (leaf node)
                                    scrollToCategory(subCategory.name);
                                    closeMobileMenuIfMobile();
                                    onCategorySelect?.(
                                      subCategory.id,
                                      subCategory.name,
                                    );
                                  }
                                }}
                                aria-label={subCategory.name}
                              >
                                {/* Light blue vertical bar for all 3rd level categories with hover and selected effects */}
                                <div
                                  className={`absolute left-[64px] top-0 bottom-0 w-[2px] transition-colors ${
                                    selectedCategory === subCategory.id
                                      ? "bg-[#352FC3]"
                                      : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                                  }`}
                                ></div>
                                {/* Category Name */}
                                <span
                                  className={`flex-1 label-m-medium ${
                                    selectedCategory === subCategory.id
                                      ? "text-fg-brand-subtle"
                                      : "text-fg-neutral-strong"
                                  }`}
                                >
                                  {subCategory.name}
                                </span>

                                {/* Expand/Collapse Button for Level 3 if it has children */}
                                {subCategory.categoryOptions &&
                                  subCategory.categoryOptions.length > 0 && (
                                    expandedCategories.has(subCategory.id)
                                      ? <CaretUpIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                                      : <CaretDownIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                                  )}
                              </button>

                              {/* Level 4+ (if subCategory has more children, render recursively) */}
                              {expandedCategories.has(subCategory.id) &&
                                subCategory.categoryOptions && (
                                  <div role="group" className="bg-bg-brand-secondary-default">
                                    {renderCategory(subCategory, level + 2)}
                                  </div>
                                )}
                            </div>
                          ))}
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Scrollbar styles for webkit browsers (Chrome, Safari, Edge) */}
      <style>{`
 .scrollbar-hide::-webkit-scrollbar {
 width: 8px;
 height: 8px;
 }
 .scrollbar-hide::-webkit-scrollbar-track {
 background: transparent;
 }
 .scrollbar-hide::-webkit-scrollbar-thumb {
 background: transparent;
 border-radius: 4px;
 }
 .scrollbar-hide:hover::-webkit-scrollbar-thumb {
 background: #CBD5E1;
 }
 .scrollbar-hide:hover::-webkit-scrollbar-thumb:hover {
 background: #94A3B8;
 }`}</style>

      <div
        ref={sidebarRef}
        className={`relative overflow-hidden bg-bg-brand-secondary-default w-full md:w-[300px] lg:w-[350px] ${showMobileMenu ? "flex h-full" : isZoomedIn ? "flex h-screen" : "hidden md:flex md:h-full"} border-r-0 md:border-r border-gray-200 flex-col flex-shrink-0 min-h-0`}
        style={
          isZoomedIn && !showMobileMenu
            ? {
                height: "100vh",
                maxHeight: "100vh",
              }
            : undefined
        }
        role={showMobileMenu ? "dialog" : undefined}
        aria-modal={showMobileMenu ? "true" : undefined}
        aria-label={showMobileMenu ? "Categories menu" : undefined}
      >
        {/* Mobile Header with Close Button */}
        {(showMobileMenu || isZoomedIn) && (
          <div className={`${!isZoomedIn ? "md:hidden" : ""} px-4 md:px-6 pt-3 pb-2 border-b border-gray-200 flex-shrink-0`}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="label-m-semibold text-fg-neutral-strong">
                {i18n.get("categories")}
              </h2>
              <button
                onClick={() => {
                  // nav_tree_closed — the sidebar's own X (parent fires with venue context).
                  onNavTreeClose?.();
                  setShowMobileMenu(false);
                }}
                className="p-2 hover:bg-bg-brand-secondary-hover rounded-lg transition-colors"
                aria-label="Close categories menu"
              >
                <XCircleIcon size={24} className="text-fg-neutral-strong" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="px-4 md:px-6 py-4 flex-shrink-0">
          <div className="relative">
            <label htmlFor="category-search" className="sr-only">
              {searchPlaceholder} 
            </label>
            <input
              id="category-search"
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full h-12 pl-4 pr-10 py-2 md:py-3 text-sm md:text-[13px] border border-gray-400 rounded-lg bg-bg-brand-secondary-default text-gray-900 placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm ? (
              <button
                onClick={() => {
                  setSearchTerm("");
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-fg-neutral-strong hover:text-fg-neutral-strong transition-colors p-1"
                aria-label="Clear search"
              >
                <XCircleIcon size={22} aria-hidden="true" />
              </button>
            ) : (
              <MagnifyingGlassIcon
                size={22}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-fg-neutral-strong"
                aria-hidden="true"
              />
            )}
          </div>
        </div>

        {/* Filter Slot - optional filter buttons passed by parent (e.g. on space detail page) */}
        {filterSlot && (
          <div className="px-4 md:px-6 pb-3 flex items-center gap-2 flex-shrink-0">
            {filterSlot}
          </div>
        )}

        {/* Navigation Categories */}
        <nav
          role="tree"
          aria-label="Space categories"
          className="overflow-y-auto flex-1 relative scrollbar-hide hover:scrollbar-show"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
          }}
          onKeyDown={handleTreeKeyDown}
          onMouseEnter={(e) => {
            e.currentTarget.style.scrollbarColor = "#CBD5E1 transparent";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.scrollbarColor = "transparent transparent";
          }}
        >
          <div className="pb-20" style={{ paddingBottom: "300px" }}>
            {reviewTags.map((category) => renderCategory(category))}
          </div>
        </nav>

        {/* Mobile spaces slide-over — slides in over the category tree */}
        <div
          className={`absolute inset-0 z-10 bg-bg-brand-secondary-default flex flex-col transform transition-transform duration-300 ease-in-out lg:hidden ${
            mobileSpacesView ? "translate-x-0" : "translate-x-full"
          }`}
          aria-hidden={!mobileSpacesView}
        >
          <div className="px-4 pt-3 pb-2 border-b border-gray-200 flex-shrink-0 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileSpacesView(null)}
              className="p-2 hover:bg-bg-brand-secondary-hover rounded-lg transition-colors"
              aria-label="Back to categories"
            >
              <CaretLeftIcon size={20} className="text-fg-neutral-strong" aria-hidden="true" />
            </button>
            <span className="label-m-semibold text-fg-neutral-strong truncate">
              {mobileSpacesView?.categoryName}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="border-l-2 border-border-brand-subtle mx-6 mt-4 flex flex-col">
              {mobileSpacesView?.spaces.map((space) => (
                <button
                  key={`mobile-${mobileSpacesView.categoryId}-${space.id}`}
                  type="button"
                  className="w-full flex items-center gap-2 px-4 py-3 text-left text-fg-neutral-strong hover:bg-bg-neutral-tertiary-hover transition-colors focus-blue"
                  onClick={() => handleMobileSpaceSelect(space)}
                >
                  <span className="flex-1 min-w-0 label-m-medium">{space.level}</span>
                  <CaretRightIcon size={18} className="text-fg-neutral-subtle flex-shrink-0" aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {hoveredSpacesCategory && hoveredSpacesCategory.spaces.length > 0 && (
        <div
          className="fixed z-[1400] w-[320px] bg-bg-brand-secondary-default border-x border-border-token-neutral-minimal p-6 flex flex-col"
          style={{
            top: spacesPanelBounds.top,
            left: spacesPanelBounds.left,
            height: spacesPanelBounds.height,
            maxHeight: spacesPanelBounds.height,
          }}
          onMouseEnter={keepSpacesPanelOpen}
          onMouseLeave={handleSpacesHoverLeave}
        >
          <div className="label-m-semibold text-fg-neutral-strong mb-4">
            {hoveredSpacesCategory.categoryName}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="border-l-2 border-border-brand-subtle flex flex-col">
              {hoveredSpacesCategory.spaces.map((space) => (
                <button
                  key={`${hoveredSpacesCategory.categoryId}-${space.id}`}
                  type="button"
                  className="w-full flex items-center gap-2 px-4 py-3 text-left text-fg-neutral-strong hover:bg-bg-neutral-tertiary-hover transition-colors focus-blue"
                  onClick={() => handleSpaceSelect(space)}
                >
                  <span className="flex-1 min-w-0 label-m-medium">
                    {space.level}
                  </span>
                  <CaretRightIcon
                    size={18}
                    className="text-fg-neutral-subtle flex-shrink-0"
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CategorySidebar;
